import './Search.scss';
import React, { HTMLProps, ReactNode, useEffect, useState } from 'react';
import { MdClose, MdSearch } from 'react-icons/md';
import clsx from 'clsx';
import { useDebounce } from '../../hooks/useDebounce';
import Button from '../../atoms/Button';
import Input from '../../atoms/Input';

export interface ISearchProps extends HTMLProps<HTMLInputElement> {
    /** Возможность очистки поля по клику */
    onClear?: () => void;
    /** Показать иконку очистки */
    showClear?: boolean;
    /** Дебаунс */
    debounce?: number;
    /** Иконка в конце поля */
    endAdornment?: ReactNode;
    /** Значение после паузы ввода; очистка — сразу `''` */
    onDebouncedChange?: (value: string) => void;
    /**
     * Проверять ввод в соответствии с регулярным выражением
     * @example Для проверки на отсутствие спецсимволов в строке можно использовать `'^[\da-zA-Zа-яА-Я]*$'`
     */
    pattern?: string;
    /**
     * Является ли Search компонентом HistoryCardFilter
     * @default false
     */
    isCardFilter?: boolean;
    /** Дополнительный контент для отображения под полем поиска */
    dropdownContent?: ReactNode;
    /** Показывать ли дополнительный контент */
    showDropdown?: boolean;
    /** Обработчик клика по полю поиска */
    onInputClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
    /** Обработчик события onKeyDown */
    onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void | Promise<void>;
    /** Обработчик события клика по иконке поиска */
    onSearchIconClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
    /**
     * Вид: `header` — поле шапки на градиенте оболочки: стекло кнопок шапки (`--shell-button-*`) в покое, белое поле
     * в фокусе и с текстом
     * @default default
     */
    variant?: 'default' | 'header';
    /** Акцент фокуса поля шапки: `ai` — фиолетовый (включён поиск ИИ) */
    tone?: 'ai';
}

// Фасад: поле — атом Input (Mantine TextInput) c variant="unstyled" —
// пилюля поиска намеренно не похожа на стандартный Input, unstyled выводит её из-под
// глобальной темы инпутов; визуал целиком в Search.scss. Лупа и блок очистки/endAdornment —
// абсолютные оверлеи рядом с полем, как в легаси, но сам input теперь вложен в
// Mantine-обёртки, поэтому связи «сосед по DOM» (input:focus + лупа) в SCSS переведены
// на :has(). Дебаунс и очистка остаются собственными:
// clear через state сбрасывает висящий таймер дебаунса (легаси-семантика), pattern-фильтр
// делегирован Input (та же семантика отклонения символа).
const Search: React.FC<ISearchProps> = ({
    onClear,
    showClear = true,
    debounce = 500,
    endAdornment,
    onDebouncedChange = () => {},
    pattern,
    isCardFilter = false,
    dropdownContent,
    showDropdown = false,
    onInputClick,
    onInputKeyDown,
    onSearchIconClick,
    variant = 'default',
    tone,
    disabled,
    // size (число знаков) из HTMLProps несовместим с Mantine-размером;
    // легаси его фактически игнорировал (атрибут не влиял при width: 100%)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    size: _size,
    ...props
}: ISearchProps) => {
    const [value, setValue] = useState<string>(props.value ? props.value.toString() : '');

    useDebounce(value, debounce, (v) => {
        if (onDebouncedChange) {
            onDebouncedChange(v.toString());
        }
    });

    useEffect(() => {
        setValue(props.value ? props.value.toString() : '');
    }, [props.value]);

    const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        props.onChange && props.onChange(e);
    };

    const onClearClickHandler = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        e.preventDefault();

        setValue('');
        onClear && onClear();
        onDebouncedChange && onDebouncedChange('');
    };

    return (
        <div
            className={clsx(
                'rf-search',
                isCardFilter && 'rf-search-hcf',
                variant === 'header' && 'rf-search--header',
                tone === 'ai' && 'rf-search--ai',
            )}
        >
            <Input
                {...props}
                disabled={disabled}
                type="text"
                variant="unstyled"
                classNames={{
                    root: 'rf-search__field',
                    wrapper: 'rf-search__field-wrapper',
                    input: clsx('rf-search__input', endAdornment && 'rf-search__input-endAdornment'),
                }}
                placeholder={props.placeholder || 'Поиск'}
                value={value}
                data-testid="search-test-id"
                onChange={onChangeHandler}
                onClick={onInputClick}
                pattern={pattern}
                onKeyDown={(e) => {
                    void onInputKeyDown?.(e);
                }}
            />
            <Button
                variant="icon-round"
                size="xs"
                className="rf-search__search-icon"
                aria-label="Найти"
                onClick={(e) => {
                    void onSearchIconClick?.(e);
                }}
                startAdornment={<MdSearch size="24" />}
            />
            <div className="rf-search__endAdornment">
                {value.length > 0 && showClear && (
                    <MdClose
                        data-testid="search-clear-test-id"
                        className={clsx('rf-search__close-icon')}
                        onMouseDown={onClearClickHandler}
                    />
                )}
                {endAdornment}
            </div>

            {showDropdown && dropdownContent}
        </div>
    );
};

export default Search;
